<template><div><component-combinator component-name="DtCheckbox" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Checkboxes are an easily understandable way to indicate that users can select one or more answers to a question or items from a list. They visibly show users what's been selected and makes it easy for them to &quot;uncheck&quot; an option, which can be difficult with other selection methods on a form (such as radio buttons or select menus).</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Selecting any number of choices from a set list.</li>
<li class="d-docsite--list-element">Binary selections that convey opposite states, such as check=&quot;on&quot; and unchecked=&quot;off&quot;, paired with a label that conveys the choice.</li>
<li class="d-docsite--list-element">When users need to see all the available options at a glance.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If a user can only select one option from a list; consider using <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink> or <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select</RouteLink>.</li>
<li class="d-docsite--list-element">If there are too many options to reasonably display in its context.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Users should be able to tap on or click on either the text <code v-pre>label</code> or the checkbox element itself to toggle an option, aiding accessibility.</li>
<li class="d-docsite--list-element">Options that are listed vertically are easier to read than those listed horizontally. Horizontal listings can make it difficult to tell which label pertains to which checkbox.</li>
<li class="d-docsite--list-element">Negative language in labels can be counterintuitive. For example, use &quot;I want to receive a promotional email&quot; instead of &quot;I don't want to receive promotional email&quot;.</li>
<li class="d-docsite--list-element">Make sure that the label makes both states — checked and unchecked — clear to the user. If that's not possible, consider using a <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink> button with two individual options instead. Then both states can have their own clearly marked label.</li>
<li class="d-docsite--list-element">Make sure selections are adequately spaced for touch screens.</li>
<li class="d-docsite--list-element">Multiple checkbox options should be organized in a meaningful way, like alphabetical or most-frequent to least-frequent. This helps users easily find the option they're looking for.</li>
<li class="d-docsite--list-element"><code v-pre>indeterminate</code> checkboxes convey a &quot;mixed&quot; state that neither qualifies as checked or unchecked. An example use case is when a checkbox acts as a &quot;parent&quot; of a collection of child checkboxes:
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>checked</code>: all children are checked.</li>
<li class="d-docsite--list-element"><code v-pre>unchecked</code>: all children are not checked.</li>
<li class="d-docsite--list-element"><code v-pre>indeterminate</code>: children are a mix of checked and unchecked.</li>
</ul>
</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;!-- Default --&gt;
&lt;dt-checkbox
  name=&quot;default&quot;
  value=&quot;Value&quot;
  label=&quot;Checkbox label&quot;
/&gt;
&lt;!-- Checked --&gt;
&lt;dt-checkbox
  name=&quot;checked&quot;
  value=&quot;Value&quot;
  label=&quot;Checkbox label&quot;
  checked
/&gt;
&lt;!-- Disabled --&gt;
&lt;dt-checkbox
  name=&quot;disabled&quot;
  value=&quot;Value&quot;
  label=&quot;Disabled Checkbox label&quot;
  disabled
/&gt;
&lt;!-- Disabled Checked --&gt;
&lt;dt-checkbox
  name=&quot;disabled-checked&quot;
  value=&quot;Value&quot;
  label=&quot;Disabled Checkbox label&quot;
  checked
  disabled
/&gt;'>
<dt-stack data-demo-wrapper gap="100">
  <!-- Default -->
  <dt-checkbox
    name="default"
    value="Value"
    label="Checkbox label"
  />
  <!-- Checked -->
  <dt-checkbox
    name="checked"
    value="Value"
    label="Checkbox label"
    checked
  />
  <!-- Disabled -->
  <dt-checkbox
    name="disabled"
    value="Value"
    label="Disabled Checkbox label"
    disabled
  />
  <!-- Disabled Checked -->
  <dt-checkbox
    name="disabled-checked"
    value="Value"
    label="Disabled Checkbox label"
    checked
    disabled
  />
</dt-stack>
</code-example>
<h3 id="indeterminate" tabindex="-1" class="d-docsite--header-3">Indeterminate <a class="header-anchor d-link d-docsite--link d-link" href="#indeterminate" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;!-- Indeterminate --&gt;
&lt;dt-checkbox
  name=&quot;indeterminate&quot;
  value=&quot;Value&quot;
  label=&quot;Indeterminate checkbox&quot;
  indeterminate
/&gt;
&lt;!-- Indeterminate disabled --&gt;
&lt;dt-checkbox
  name=&quot;indeterminate-disabled&quot;
  value=&quot;Value&quot;
  label=&quot;Indeterminate checkbox disabled&quot;
  checked
  disabled
  indeterminate
/&gt;'>
<dt-stack data-demo-wrapper gap="100">
  <!-- Indeterminate -->
  <dt-checkbox
    name="indeterminate"
    value="Value"
    label="Indeterminate checkbox"
    indeterminate
  />
  <!-- Indeterminate disabled -->
  <dt-checkbox
    name="indeterminate-disabled"
    value="Value"
    label="Indeterminate checkbox disabled"
    checked
    disabled
    indeterminate
  />
</dt-stack>
</code-example>
<h3 id="stacked-group" tabindex="-1" class="d-docsite--header-3">Stacked Group <a class="header-anchor d-link d-docsite--link d-link" href="#stacked-group" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group legend=&quot;Call Blocking &amp; Spam Protection&quot; :selectedValues=&quot;[]&quot;&gt;
  &lt;dt-checkbox
    name=&quot;option1&quot;
    value=&quot;Value&quot;
    label=&quot;Anonymous callers&quot;
  /&gt;
  &lt;dt-checkbox
    name=&quot;option2&quot;
    value=&quot;Value&quot;
    label=&quot;Block callers not already in contacts list&quot;
  /&gt;
  &lt;dt-checkbox
    name=&quot;option3&quot;
    value=&quot;Value&quot;
    label=&quot;Block callers with a high spam score&quot;
  /&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group legend="Call Blocking & Spam Protection" :selectedValues="[]">
  <dt-checkbox
    name="option1"
    value="Value"
    label="Anonymous callers"
  />
  <dt-checkbox
    name="option2"
    value="Value"
    label="Block callers not already in contacts list"
  />
  <dt-checkbox
    name="option3"
    value="Value"
    label="Block callers with a high spam score"
  />
</dt-checkbox-group>
</code-example>
<h3 id="with-description-text" tabindex="-1" class="d-docsite--header-3">With Description Text <a class="header-anchor d-link d-docsite--link d-link" href="#with-description-text" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group legend=&quot;Call Blocking &amp; Spam Protection&quot; :selectedValues=&quot;[]&quot;&gt;
  &lt;dt-checkbox
    name=&quot;option1&quot;
    value=&quot;Value&quot;
    label=&quot;Anonymous callers&quot;
    description=&quot;Select how phone numbers you dont know should be handled.&quot;
  /&gt;
  &lt;dt-checkbox
    name=&quot;option2&quot;
    value=&quot;Value&quot;
    label=&quot;Block callers not already in contacts list&quot;
    description=&quot;You get enough calls. Free up some of your time.&quot;
  /&gt;
  &lt;dt-checkbox
    name=&quot;option3&quot;
    value=&quot;Value&quot;
    label=&quot;Block callers with a high spam score&quot;
    description=&quot;We will only let the legitimate callers through to bother you.&quot;
  /&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group legend="Call Blocking & Spam Protection" :selectedValues="[]">
  <dt-checkbox
    name="option1"
    value="Value"
    label="Anonymous callers"
    description="Select how phone numbers you dont know should be handled."
  />
  <dt-checkbox
    name="option2"
    value="Value"
    label="Block callers not already in contacts list"
    description="You get enough calls. Free up some of your time."
  />
  <dt-checkbox
    name="option3"
    value="Value"
    label="Block callers with a high spam score"
    description="We will only let the legitimate callers through to bother you."
  />
</dt-checkbox-group>
</code-example>
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-checkbox-group legend=&quot;Call Blocking &amp; Spam Protection&quot; :selectedValues=&quot;[]&quot;&gt;
  &lt;dt-checkbox
    name=&quot;option1&quot;
    value=&quot;Value&quot;
    label=&quot;Anonymous callers&quot;
    validation-state=&quot;warning&quot;
    :messages=&quot;[{ message: `Select how phone numbers you dont know should be handled.`, type: `warning` }]&quot;
  /&gt;
  &lt;dt-checkbox
    name=&quot;option2&quot;
    value=&quot;Value&quot;
    label=&quot;Block callers not already in contacts list&quot;
    validation-state=&quot;critical&quot;
    :messages=&quot;[{ message: `You get enough calls. Free up some of your time.`, type: `critical` }]&quot;
  /&gt;
  &lt;dt-checkbox
    name=&quot;option3&quot;
    value=&quot;Value&quot;
    label=&quot;Block callers with a high spam score&quot;
    validation-state=&quot;positive&quot;
    :messages=&quot;[{ message: `We will only let the legitimate callers through to bother you.`, type: `positive` }]&quot;
  /&gt;
&lt;/dt-checkbox-group&gt;'>
<dt-checkbox-group legend="Call Blocking & Spam Protection" :selectedValues="[]">
  <dt-checkbox
    name="option1"
    value="Value"
    label="Anonymous callers"
    validation-state="warning"
    :messages="[{ message: `Select how phone numbers you dont know should be handled.`, type: `warning` }]"
  />
  <dt-checkbox
    name="option2"
    value="Value"
    label="Block callers not already in contacts list"
    validation-state="critical"
    :messages="[{ message: `You get enough calls. Free up some of your time.`, type: `critical` }]"
  />
  <dt-checkbox
    name="option3"
    value="Value"
    label="Block callers with a high spam score"
    validation-state="positive"
    :messages="[{ message: `We will only let the legitimate callers through to bother you.`, type: `positive` }]"
  />
</dt-checkbox-group>
</code-example>
<h2 id="label-size" tabindex="-1" class="d-docsite--header-2">Label size <a class="header-anchor d-link d-docsite--link d-link" href="#label-size" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the <code v-pre>label-size</code> prop to override the default label size.</p>
<code-example source-code='&lt;dt-checkbox name=&quot;sizeXs&quot; value=&quot;Value&quot; label=&quot;Extra small label&quot; :label-size=&quot;100&quot; /&gt;
&lt;dt-checkbox name=&quot;sizeSm&quot; value=&quot;Value&quot; label=&quot;Small label&quot; :label-size=&quot;200&quot; /&gt;
&lt;dt-checkbox name=&quot;sizeMd&quot; value=&quot;Value&quot; label=&quot;Medium label (default)&quot; :label-size=&quot;300&quot; /&gt;
&lt;dt-checkbox name=&quot;sizeLg&quot; value=&quot;Value&quot; label=&quot;Large label&quot; :label-size=&quot;400&quot; /&gt;'>
<dt-stack data-demo-wrapper gap="100">
  <dt-checkbox name="sizeXs" value="Value" label="Extra small label" :label-size="100" />
  <dt-checkbox name="sizeSm" value="Value" label="Small label" :label-size="200" />
  <dt-checkbox name="sizeMd" value="Value" label="Medium label (default)" :label-size="300" />
  <dt-checkbox name="sizeLg" value="Value" label="Large label" :label-size="400" />
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="checkbox" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="checkbox"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The best accessibility is semantic HTML. Most screen readers understand how to parse inputs if they're correctly formatted. When it comes to checkboxes, there are a few things to keep in mind:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">All inputs should have an <code v-pre>id</code> attribute.</li>
<li class="d-docsite--list-element">Associate checkbox labels with their inputs using the <code v-pre>for</code> attribute. This correlates with the checkbox's <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">If you have a group of related checkboxes, use a <code v-pre>fieldset</code> to group them and a <code v-pre>legend</code> to title the group. For further information, please visit Gov.UK's article, <a href="https://accessibility.blog.gov.uk/2016/07/22/using-the-fieldset-and-legend-elements" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">&quot;Using the fieldset and legend elements&quot;</a>.</li>
<li class="d-docsite--list-element">Input with description text should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the description text.</li>
<li class="d-docsite--list-element">If the input is a required field, use the <code v-pre>aria-required</code> property and use the validation message for input errors.</li>
<li class="d-docsite--list-element">Input with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
</ul>
</div></template>


